import React, { useState } from 'react';
import { Info } from 'lucide-react';

interface InfoTooltipProps {
  message: string;
  position?: 'top' | 'bottom' | 'left' | 'right';
  className?: string;
}

export const InfoTooltip: React.FC<InfoTooltipProps> = ({
  message,
  position = 'top',
  className = ''
}) => {
  const [showTooltip, setShowTooltip] = useState(false);

  const getPositionClasses = () => {
    switch (position) {
      case 'bottom':
        return 'top-full left-1/2 -translate-x-1/2 mt-2';
      case 'left':
        return 'top-1/2 -translate-y-1/2 right-full mr-2';
      case 'right':
        return 'top-1/2 -translate-y-1/2 left-full ml-2';
      case 'top':
      default:
        return 'bottom-full left-1/2 -translate-x-1/2 mb-2';
    }
  };
  
  // Basic arrow, can be improved with ::before/::after pseudo-elements if more complex arrow is needed
  const getArrowClasses = () => {
    switch (position) {
      case 'bottom':
        return 'absolute bottom-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-b-4 border-l-transparent border-r-transparent border-b-primary-100';
      case 'left':
        return 'absolute top-1/2 -translate-y-1/2 left-full w-0 h-0 border-t-4 border-b-4 border-l-4 border-t-transparent border-b-transparent border-l-primary-100';
      case 'right':
        return 'absolute top-1/2 -translate-y-1/2 right-full w-0 h-0 border-t-4 border-b-4 border-r-4 border-t-transparent border-b-transparent border-r-primary-100';
      case 'top':
      default:
        return 'absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-primary-100';
    }
  }

  return (
    <div 
      className={`relative inline-flex items-center ${className}`}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
      onFocus={() => setShowTooltip(true)}
      onBlur={() => setShowTooltip(false)}
      tabIndex={0} // Make it focusable
      role="tooltip"
      aria-describedby={showTooltip ? "info-tooltip-content" : undefined}
    >
      <Info className="w-4 h-4 text-gray-500 hover:text-gray-700 cursor-pointer" />
      {showTooltip && (
        <div
          id="info-tooltip-content"
          className={`absolute ${getPositionClasses()} w-max max-w-xs z-50 px-3 py-1.5 bg-primary-100 text-gray-900 text-xs rounded-md shadow whitespace-normal`}
        >
          {message}
          <div className={getArrowClasses()}></div>
        </div>
      )}
    </div>
  );
};

// Export as default if preferred, or keep as named
// export default InfoTooltip; 